import {
  FIRST_INSTALL_ATTRIBUTION_SOURCES,
  FIRST_INSTALL_ONBOARDING_SETUP_SOURCE,
  FIRST_INSTALL_ONBOARDING_VERSION,
  type FirstInstallAttributionSource,
  type FirstInstallOnboardingDestination,
  type Onboarding,
} from "@redotech/account-common/onboarding";

export type FirstInstallStep = "terms" | "attribution" | "product";
export type FirstInstallNextAction = FirstInstallStep | "complete" | null;
export interface FirstInstallNativeSetupGuideStart {
  product: "disputes";
  entrySource: typeof FIRST_INSTALL_ONBOARDING_SETUP_SOURCE;
}

export interface FirstInstallSetupGuideStarter {
  startGuide(
    product: FirstInstallNativeSetupGuideStart["product"],
    entrySource: FirstInstallNativeSetupGuideStart["entrySource"],
    options?: { forceResetIntake?: boolean },
  ): Promise<void>;
}

export function getInitialFirstInstallStep(
  data: Pick<Onboarding, "tcConfirmed" | "firstInstallOnboarding">,
): FirstInstallStep | null {
  if (!data.tcConfirmed) {
    return "terms";
  }
  if (
    data.firstInstallOnboarding?.version === FIRST_INSTALL_ONBOARDING_VERSION &&
    !data.firstInstallOnboarding.completedAt
  ) {
    return "attribution";
  }
  return null;
}

export function getFirstInstallContinueText(
  step: FirstInstallStep | null,
  pending: boolean,
): string {
  if (pending) {
    return "Setting up...";
  }
  switch (step) {
    case "terms":
      return "Accept and continue";
    default:
      return "Continue";
  }
}

export function getNextFirstInstallAction(args: {
  step: FirstInstallStep | null;
  productSelectionRequired: boolean;
}): FirstInstallNextAction {
  switch (args.step) {
    case "terms":
      return "attribution";
    case "attribution":
      return args.productSelectionRequired ? "product" : "complete";
    case "product":
      return "complete";
    default:
      return null;
  }
}

export function toFirstInstallAttributionSource(
  value: string,
): FirstInstallAttributionSource | null {
  for (const source of FIRST_INSTALL_ATTRIBUTION_SOURCES) {
    if (source === value) {
      return source;
    }
  }
  return null;
}

export function getFirstInstallNativeSetupGuideStart(
  destination: FirstInstallOnboardingDestination,
): FirstInstallNativeSetupGuideStart | null {
  if (destination.type !== "merchant") {
    return null;
  }
  if (!destination.path.startsWith("/") || destination.path.startsWith("//")) {
    return null;
  }

  const url = new URL(destination.path, "https://merchant.getredo.com");
  if (
    url.searchParams.get("setupSource") !==
    FIRST_INSTALL_ONBOARDING_SETUP_SOURCE
  ) {
    return null;
  }
  if (
    !/\/(?:finance\/disputes|payments\/chargebacks)(?:\/|$)/.test(url.pathname)
  ) {
    return null;
  }
  return {
    product: "disputes",
    entrySource: FIRST_INSTALL_ONBOARDING_SETUP_SOURCE,
  };
}

export async function startFirstInstallNativeSetupGuideIfAvailable(
  destination: FirstInstallOnboardingDestination,
  setupGuide: FirstInstallSetupGuideStarter | null,
): Promise<boolean> {
  const nativeSetupGuideStart =
    getFirstInstallNativeSetupGuideStart(destination);
  if (!nativeSetupGuideStart || !setupGuide) {
    return false;
  }

  await setupGuide.startGuide(
    nativeSetupGuideStart.product,
    nativeSetupGuideStart.entrySource,
    { forceResetIntake: true },
  );
  return true;
}
